import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { resetLoginRedirectLock } from "../../api";
import { useAuth } from "./AuthContext";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const LoginPage = () => {
  const { t }                 = useTranslation();
  const [form, setForm]       = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const { login }             = useAuth();
  const navigate              = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await api.post("/auth/login", form);
      login(res.data.user, res.data.token);
      resetLoginRedirectLock();
      navigate("/", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 401
          ? t("auth.badCreds")
          : t("auth.connError")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ── Left panel — branding ── */}
      <div className="hidden lg:flex lg:w-[45%] relative flex-col items-center justify-center p-12 overflow-hidden bg-slate-900">
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />
        {/* Orange glow top-left */}
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-orange-500 rounded-full opacity-20 blur-3xl" />
        {/* Orange glow bottom-right */}
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-orange-400 rounded-full opacity-10 blur-3xl" />

        <div className="relative z-10 flex flex-col items-center gap-8 text-center max-w-xs">
          {/* Logo */}
          <div className="w-20 h-20 rounded-2xl overflow-hidden border-2 border-white/10 shadow-2xl bg-white">
            <img src="/logo.jpg" alt="The Movers" className="w-full h-full object-contain" />
          </div>

          <div>
            <h1 className="text-4xl font-black tracking-tight text-white">The Movers</h1>
            <p className="text-orange-400 mt-1.5 text-base font-medium">{t("auth.tagline")}</p>
          </div>

          <p className="text-slate-400 text-sm leading-relaxed">
            {t("auth.pitch")}
          </p>

          {/* Feature list */}
          <ul className="space-y-2.5 text-left w-full">
            {[
              t("auth.feat1"),
              t("auth.feat2"),
              t("auth.feat3"),
            ].map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm text-slate-400">
                <span className="w-5 h-5 rounded-full bg-orange-500/20 border border-orange-500/30 flex items-center justify-center text-orange-400 shrink-0 text-[10px]">
                  ✓
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Right panel — form ── */}
      <div className="flex-1 flex items-center justify-center bg-base-200 px-6 py-12">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center mb-8 gap-3">
            <div className="w-14 h-14 rounded-xl overflow-hidden border border-base-200 shadow-md bg-base-100">
              <img src="/logo.jpg" alt="The Movers" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-xl font-extrabold text-base-content">The Movers</h1>
          </div>

          <div className="bg-base-100 rounded-2xl border border-base-200 shadow-sm p-8">
            <div className="mb-7">
              <h2 className="text-xl font-bold text-base-content">{t("auth.login")}</h2>
              <p className="text-sm text-base-content/50 mt-1">{t("auth.loginSub")}</p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {/* Username */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-base-content/80">
                  {t("auth.username")}
                </label>
                <input
                  type="text"
                  autoFocus
                  autoComplete="username"
                  className="input input-bordered w-full focus:border-primary focus:outline-none bg-base-200 border-base-200"
                  placeholder={t("auth.usernamePlaceholder")}
                  value={form.username}
                  onChange={e => setForm({ ...form, username: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>

              {/* Password */}
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-semibold text-base-content/80">
                  {t("auth.password")}
                </label>
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    autoComplete="current-password"
                    className="input input-bordered w-full pr-11 focus:border-primary focus:outline-none bg-base-200 border-base-200"
                    placeholder="••••••••"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/50 hover:text-base-content/70 transition-colors"
                    onClick={() => setShowPwd(v => !v)}
                    tabIndex={-1}
                  >
                    {showPwd ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg px-3.5 py-2.5">
                  <span className="text-red-500 text-sm mt-0.5 shrink-0">⚠</span>
                  <span className="text-red-600 text-sm">{error}</span>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary w-full mt-2 font-semibold shadow-sm"
                style={{ background: "#f97316", borderColor: "#f97316" }}
              >
                {loading && <span className="loading loading-spinner loading-sm" />}
                {loading ? t("auth.connecting") : t("auth.submit")}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-base-content/50 mt-5">
            © {new Date().getFullYear()} The Movers — {t("auth.rights")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
